import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Container,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
} from "@mui/material";

import TabletIcon from "../../../Assets/Icons/tablet-icon.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import httpclient from "../../../Utils";
import { isEmptyObject } from "../../../Utils/utils";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AddStation() {
  const [tabIndex, setTabIndex] = useState(0);

  const [reasonInfo, setReasonInfo] = useState({
    name: "",
    isReasonActive: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const { reason } = location.state || {};
  const hasPDevice = !isEmptyObject(reason);

  const [mode, setMode] = useState(hasPDevice ? "edit" : "add");

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  useEffect(() => {
    if (mode === "edit") {
      const { reason_name, status } = reason;

      setReasonInfo({
        name: reason_name,
        isReasonActive: status,
      });
      setMode("edit");
    } else {
      setMode("add");
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleDeviceInfoChange = (event) => {
    const { name, value } = event.target;

    setReasonInfo((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleReasonDisableChange = (event) => {
    setReasonInfo((prevData) => ({
      ...prevData,
      isReasonActive: event.target.checked,
    }));
  };

  const validateForm = () => {
    const rules = [
      { condition: !reasonInfo.name, message: "Station Name is required" },
    ];

    for (const rule of rules) {
      if (rule.condition) {
        showNotification("error", rule.message);
        return false;
      }
    }
    return true;
  };

  const getFormattedData = (data, isEdit) => {
    const formData = new FormData();

    const dataMap = {
      reason_name: data.name,
      status: data.isReasonActive ? 1 : 0,
    };

    Object.entries(dataMap).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return isEdit ? dataMap : formData;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const isEdit = mode === "edit";

    const endpoint = isEdit
      ? `/hold-reason/update/${reason.id}`
      : "/hold-reason/add";

    try {
      setIsSubmitting(true);

      const reasonData = getFormattedData(reasonInfo, isEdit);

      const res = await httpclient.post(endpoint, reasonData);

      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        navigate("/hold-reason");
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error, setIsSubmitting);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setReasonInfo({
      name: "",
      isReasonActive: false,
    });

    setTimeout(() => {
      navigate("/hold-reason");
    }, 100);
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="hold reason detail tabs"
          >
            <Tab
              icon={<img src={TabletIcon} alt="Tablet icon" />}
              iconPosition="start"
              label="Hold Reason Detail"
              id="tab-0"
              aria-controls="tabpanel-0"
              sx={{
                "&.Mui-selected": { color: "#7152F3" },
              }}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabIndex} index={0}>
          <Box component="form" onSubmit={handleSave} noValidate>
            <Typography
              component="div"
              color="secondary"
              variant="caption"
              sx={{ my: 2, fontStyle: "italic" }}
            >
              Field marked * is required
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label="Reason Name"
                  name="name"
                  value={reasonInfo.name}
                  onChange={handleDeviceInfoChange}
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={reasonInfo.isReasonActive}
                      onChange={handleReasonDisableChange}
                      name="isReasonActive"
                    />
                  }
                  label={
                    reasonInfo.isReasonActive
                      ? "Reason is Active"
                      : "Reason is Inactive"
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ mr: 2, textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ backgroundColor: "#7152F3", textTransform: "none" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                  {mode === "edit" ? "Update" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}

export default AddStation;
